<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <Teleport to="body">
    <div v-if="props.modelValue" class="fixed inset-0 bg-black/50 z-50" @click.self="handleClose">
      <div class="flex items-center justify-center min-h-screen p-4">
        <div class="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <!-- 弹窗头部 -->
          <div class="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
            <div>
              <h3 class="text-lg font-semibold text-slate-900">{{ isEdit ? '编辑分账规则' : '新增分账规则' }}</h3>
              <p class="text-sm text-slate-500 mt-0.5">配置分账规则</p>
            </div>
            <button class="p-1 text-slate-400 hover:text-slate-600" @click="handleClose">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>

          <!-- 表单内容 -->
          <div class="flex-1 overflow-y-auto p-6">
            <div class="space-y-6">
              <!-- 基础信息 -->
              <div class="bg-white rounded-xl border border-slate-200 p-6">
                <h3 class="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <svg class="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                  </svg>
                  基础信息
                </h3>
                <div class="grid grid-cols-2 gap-4">
                  <div class="col-span-2">
                    <label class="block text-sm font-medium text-slate-700 mb-1">
                      <span class="text-red-500">*</span>规则名称
                    </label>
                    <input
                      v-model="form.ruleName"
                      type="text"
                      placeholder="请输入规则名称"
                      class="form-input w-full"
                    >
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-slate-700 mb-1">
                      <span class="text-red-500">*</span>规则类型
                    </label>
                    <select
                      v-model="form.ruleType"
                      class="form-select-native w-full"
                      @change="handleTypeChange"
                    >
                      <option v-for="t in ruleTypes" :key="t.value" :value="t.value">{{ t.label }}</option>
                    </select>
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-slate-700 mb-1">
                      优先级
                      <span class="text-slate-400 text-xs font-normal">（数值越大优先级越高）</span>
                    </label>
                    <input
                      v-model.number="form.priority"
                      type="number"
                      min="0"
                      class="form-input w-full"
                    >
                  </div>
                  <div class="col-span-2">
                    <label class="block text-sm font-medium text-slate-700 mb-1">引入人税费归属</label>
                    <select
                      v-model="form.referrerTaxReceiverRole"
                      class="form-select-native w-full"
                    >
                      <option v-for="option in taxReceiverOptions" :key="option.value" :value="option.value">
                        {{ option.label }}
                      </option>
                    </select>
                    <p class="mt-1 text-xs text-slate-500">引入人税率固定为 6%，税费由所选角色代收。</p>
                  </div>
                  <!-- 动态显示适用对象选择器 -->
                  <div v-if="form.ruleType === 'FRANCHISEE'" class="col-span-2">
                    <label class="block text-sm font-medium text-slate-700 mb-1">
                      <span class="text-red-500">*</span>适用加盟商
                    </label>
                    <FranchiseePicker v-model="form.franchiseeId" placeholder="选择适用加盟商"/>
                  </div>
                  <div v-if="form.ruleType === 'PRODUCT'" class="col-span-2">
                    <label class="block text-sm font-medium text-slate-700 mb-1">
                      <span class="text-red-500">*</span>适用产品
                    </label>
                    <ProductPicker v-model="form.productId" placeholder="选择适用产品"/>
                  </div>
                  <div class="col-span-2">
                    <label class="block text-sm font-medium text-slate-700 mb-1">备注</label>
                    <textarea
                      v-model="form.remark"
                      rows="2"
                      placeholder="请输入备注信息"
                      class="form-textarea"
                    />
                  </div>
                </div>
              </div>

              <!-- 角色分账配置 -->
              <div class="bg-white rounded-xl border border-slate-200 p-6">
                <div class="flex items-center justify-between mb-4">
                  <h3 class="text-lg font-semibold text-slate-900 flex items-center gap-2">
                    <svg class="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
                    </svg>
                    角色分账配置
                  </h3>
                  <button class="btn-primary" @click="openAddRoleForm">添加角色</button>
                </div>

                <!-- 计算基准金额 -->
                <div class="mb-4 p-4 bg-slate-50 border border-slate-200 rounded-lg">
                  <div class="flex items-center gap-4">
                    <label class="text-sm font-medium text-slate-700">计算基准金额</label>
                    <div class="flex items-center gap-2">
                      <span class="text-slate-500">¥</span>
                      <input
                        v-model.number="calculationAmount"
                        type="number"
                        min="0"
                        class="form-input w-40"
                      >
                    </div>
                    <span class="text-sm text-slate-500">根据以上配置实时计算各角色分账金额</span>
                  </div>
                </div>

                <!-- 计算顺序提示 -->
                <div class="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div class="flex items-center gap-2 text-sm text-blue-700">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                    </svg>
                    <span>计算顺序：<strong>{{ calculationOrderText }}</strong>（列表顺序即为计算顺序）</span>
                  </div>
                </div>

                <!-- 角色列表 -->
                <div v-if="sortedRoleConfigs.length === 0" class="text-center py-8 text-slate-500">
                  <svg class="w-12 h-12 mx-auto text-slate-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"/>
                  </svg>
                  <p>暂无角色配置，点击"添加角色"开始配置</p>
                </div>

                <div class="space-y-2">
                  <div v-for="(role, index) in sortedRoleConfigs" :key="role.roleCode + '_' + index" class="border border-slate-200 rounded-lg p-4 hover:border-primary-300 transition-colors">
                    <div class="flex items-center gap-3">
                      <div class="flex items-center justify-center w-6 h-6 bg-slate-100 rounded-full">
                        <span class="text-xs font-medium text-slate-600">{{ index + 1 }}</span>
                      </div>
                      <div class="flex-1 grid grid-cols-5 gap-4">
                        <div>
                          <div class="text-xs text-slate-500 mb-1">角色</div>
                          <div class="font-medium text-slate-900">{{ role.roleName }}</div>
                        </div>
                        <div>
                          <div class="text-xs text-slate-500 mb-1">分账方式</div>
                          <div class="text-sm text-slate-700">{{ getCalculateTypeLabel(role.calculateType) }}</div>
                        </div>
                        <div>
                          <div class="text-xs text-slate-500 mb-1">分账参数</div>
                          <div class="text-sm text-slate-700">{{ formatCommissionParams(role) }}</div>
                        </div>
                        <div>
                          <div class="text-xs text-slate-500 mb-1">优先级</div>
                          <div class="text-sm text-slate-700">{{ role.priority || 0 }}</div>
                        </div>
                        <div>
                          <div class="text-xs text-slate-500 mb-1">计算金额</div>
                          <div class="text-sm font-medium" :class="getCalculatedAmount(role, index) > 0 ? 'text-green-600' : 'text-slate-400'">
                            ¥{{ formatAmount(getCalculatedAmount(role, index)) }}
                          </div>
                          <div v-if="getRemainingAmount(index) > 0" class="text-xs text-slate-400 mt-1">
                            剩余: ¥{{ formatAmount(getRemainingAmount(index)) }}
                          </div>
                        </div>
                      </div>
                      <div class="flex items-center gap-1">
                        <button class="table-action-button is-primary" title="编辑" @click="editRole(role, index)">
                          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                          </svg>
                        </button>
                        <button class="table-action-button is-danger" title="删除" @click="removeRole(index)">
                          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- 弹窗底部 -->
          <div class="px-6 py-4 border-t border-slate-200 flex justify-end gap-3">
            <button class="btn-secondary" @click="handleClose">取消</button>
            <button class="btn-secondary" @click="handleSave(false)">保存</button>
            <button class="btn-primary" @click="handleSave(true)">保存并启用</button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>

  <!-- 添加/编辑角色表单弹窗 -->
  <Teleport to="body">
    <div v-if="showRoleForm" class="fixed inset-0 bg-black/50 z-50" @click.self="showRoleForm = false">
      <div class="flex items-center justify-center min-h-screen p-4">
        <div class="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
          <div class="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
            <h3 class="text-lg font-semibold text-slate-900">{{ isEditingRole ? '编辑角色' : '添加角色' }}</h3>
            <button class="p-1 text-slate-400 hover:text-slate-600" @click="closeRoleForm">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>
          <div class="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
            <div class="space-y-6">
              <!-- 角色基本信息 -->
              <div class="bg-slate-50 rounded-lg p-4">
                <h4 class="text-sm font-medium text-slate-700 mb-3">角色信息</h4>
                <div class="grid grid-cols-2 gap-4">
                  <div>
                    <label class="block text-sm font-medium text-slate-700 mb-1">
                      <span class="text-red-500">*</span>角色编码
                    </label>
                    <select
                      v-model="roleForm.roleCode"
                      :disabled="isEditingRole"
                      class="form-select-native w-full"
                    >
                      <option value="">请选择角色</option>
                      <option v-for="r in roleOptions" :key="r.value" :value="r.value">{{ r.label }}</option>
                    </select>
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-slate-700 mb-1">
                      <span class="text-red-500">*</span>角色名称
                    </label>
                    <input
                      v-model="roleForm.roleName"
                      type="text"
                      placeholder="如：推荐人"
                      class="form-input w-full"
                    >
                  </div>
                </div>
              </div>

              <!-- 分账配置 -->
              <div class="bg-slate-50 rounded-lg p-4">
                <h4 class="text-sm font-medium text-slate-700 mb-3">分账配置</h4>

                <!-- 分账方式选择 -->
                <div class="mb-4">
                  <label class="block text-sm font-medium text-slate-700 mb-2">
                    <span class="text-red-500">*</span>分账方式
                  </label>
                  <div class="grid grid-cols-5 gap-2">
                    <label
                      v-for="type in calculateTypes"
                      :key="type.value"
                      class="flex items-center gap-2 p-3 border border-slate-200 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors"
                      :class="{ 'bg-primary-50 border-primary-300': roleForm.calculateType === type.value }"
                    >
                      <input
                        v-model="roleForm.calculateType"
                        :value="type.value"
                        type="radio"
                        class="text-primary-600 focus:ring-primary-500"
                      >
                      <span class="text-sm text-slate-700">{{ type.label }}</span>
                    </label>
                  </div>
                </div>

                <!-- 固定比例分成 (RATIO) -->
                <template v-if="roleForm.calculateType === 'RATIO'">
                  <div class="space-y-4">
                    <div>
                      <label class="block text-sm font-medium text-slate-700 mb-1">
                        <span class="text-red-500">*</span>固定分成比例
                      </label>
                      <div class="flex items-center gap-2">
                        <input
                          v-model.number="roleForm.value"
                          type="number"
                          min="0"
                          max="100"
                          step="0.1"
                          class="form-input flex-1"
                        >
                        <span class="text-slate-500">%</span>
                      </div>
                    </div>
                    <div class="grid grid-cols-2 gap-4">
                      <div>
                        <label class="block text-sm font-medium text-slate-700 mb-1">最低金额（可选）</label>
                        <div class="flex items-center gap-2">
                          <span class="text-slate-500">¥</span>
                          <input
                            v-model.number="roleForm.minAmount"
                            type="number"
                            min="0"
                            class="form-input flex-1"
                          >
                        </div>
                      </div>
                      <div>
                        <label class="block text-sm font-medium text-slate-700 mb-1">最高金额（可选）</label>
                        <div class="flex items-center gap-2">
                          <span class="text-slate-500">¥</span>
                          <input
                            v-model.number="roleForm.maxAmount"
                            type="number"
                            min="0"
                            class="form-input flex-1"
                          >
                        </div>
                      </div>
                    </div>
                  </div>
                </template>

                <!-- 固定金额 (FIXED) -->
                <template v-if="roleForm.calculateType === 'FIXED'">
                  <div>
                    <label class="block text-sm font-medium text-slate-700 mb-1">
                      <span class="text-red-500">*</span>固定金额
                    </label>
                    <div class="flex items-center gap-2">
                      <span class="text-slate-500">¥</span>
                      <input
                        v-model.number="roleForm.value"
                        type="number"
                        min="0"
                        class="form-input flex-1"
                      >
                    </div>
                  </div>
                </template>

                <!-- 阶梯分成 (LADDER) -->
                <template v-if="roleForm.calculateType === 'LADDER'">
                  <div class="space-y-3">
                    <div class="text-sm text-slate-600">配置阶梯：达到门槛金额后按对应比例分成</div>
                    <div v-for="(step, index) in roleForm.ladderSteps" :key="index" class="flex items-center gap-2">
                      <div class="flex-1">
                        <input
                          v-model.number="step.threshold"
                          type="number"
                          min="0"
                          placeholder="门槛金额"
                          class="form-input w-full"
                        >
                      </div>
                      <div class="flex-1">
                        <input
                          v-model.number="step.ratio"
                          type="number"
                          min="0"
                          max="100"
                          step="0.1"
                          placeholder="比例%"
                          class="form-input w-full"
                        >
                      </div>
                      <button class="p-1.5 text-red-600 hover:bg-red-50 rounded" @click="removeLadderStep(index)">
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                        </svg>
                      </button>
                    </div>
                    <button class="w-full py-2 border border-dashed border-slate-300 text-slate-600 rounded-lg text-sm hover:bg-slate-50" @click="addLadderStep">+ 添加阶梯</button>
                  </div>
                </template>

                <!-- 最低分成 (MINIMUM) -->
                <template v-if="roleForm.calculateType === 'MINIMUM'">
                  <div>
                    <label class="block text-sm font-medium text-slate-700 mb-1">
                      <span class="text-red-500">*</span>最低金额
                    </label>
                    <div class="flex items-center gap-2">
                      <span class="text-slate-500">¥</span>
                      <input
                        v-model.number="roleForm.value"
                        type="number"
                        min="0"
                        class="form-input flex-1"
                      >
                    </div>
                    <p class="text-xs text-slate-400 mt-1">当订单金额低于此值时，按此固定金额分成</p>
                  </div>
                </template>

                <!-- 剩余分配 (REMAINDER) -->
                <template v-if="roleForm.calculateType === 'REMAINDER'">
                  <div class="p-4 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
                    该角色将获得所有前置角色分配后的剩余金额
                  </div>
                </template>

                <!-- 对剩余分配 (RELATIVE_REMAINDER) -->
                <template v-if="roleForm.calculateType === 'RELATIVE_REMAINDER'">
                  <div>
                    <label class="block text-sm font-medium text-slate-700 mb-1">
                      <span class="text-red-5">*</span>分配比例
                    </label>
                    <div class="flex items-center gap-2">
                      <input
                        v-model.number="roleForm.value"
                        type="number"
                        min="0"
                        max="100"
                        step="0.1"
                        class="form-input flex-1"
                      >
                      <span class="text-slate-500">%</span>
                    </div>
                    <p class="text-xs text-slate-400 mt-1">该角色将获得剩余金额的指定百分比</p>
                  </div>
                </template>

                <!-- 角色优先级 -->
                <div class="mt-4 pt-4 border-t border-slate-200">
                  <label class="block text-sm font-medium text-slate-700 mb-1">角色优先级</label>
                  <input
                    v-model.number="roleForm.priority"
                    type="number"
                    min="0"
                    class="form-input w-full"
                  >
                  <p class="text-xs text-slate-400 mt-1">数字越小优先级越高（用于角色计算顺序排序）</p>
                </div>
              </div>
            </div>
          </div>
          <div class="px-6 py-4 border-t border-slate-200 flex justify-end gap-3">
            <button class="btn-secondary" @click="closeRoleForm">取消</button>
            <button class="btn-primary" @click="confirmRole">确定</button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, reactive, computed, watch } from 'vue'
import { ElMessage } from 'element-plus'
import FranchiseePicker from '@/components/FranchiseeSelector/FranchiseePicker.vue'
import ProductPicker from './components/ProductSelector/ProductPicker.vue'
import { getCommissionRule, createCommissionRule, updateCommissionRule } from '@/api/commission'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  editId: {
    type: [String, Number],
    default: null
  }
})

const emit = defineEmits(['update:modelValue', 'success'])

const isEdit = computed(() => !!props.editId)

// 规则类型
const ruleTypes = [
  { value: 'GLOBAL', label: '全局规则' },
  { value: 'FRANCHISEE', label: '加盟商规则' },
  { value: 'PRODUCT', label: '产品规则' }
]

const taxReceiverOptions = [
  { value: 'PLATFORM', label: '平台代收' },
  { value: 'FRANCHISEE', label: '加盟商代收' }
]

// 角色编码选项
const roleOptions = [
  { value: 'REFERRER', label: '引入人' },
  { value: 'FRANCHISEE', label: '加盟商' },
  { value: 'PLATFORM', label: '平台' }
]

// 计算类型
const calculateTypes = [
  { value: 'RATIO', label: '固定比例' },
  { value: 'FIXED', label: '固定金额' },
  { value: 'LADDER', label: '阶梯分成' },
  { value: 'MINIMUM', label: '最低分成' },
  { value: 'REMAINDER', label: '剩余分配' },
  { value: 'RELATIVE_REMAINDER', label: '相对剩余比例' }
]

// 表单数据
const form = reactive({
  ruleName: '',
  ruleType: 'GLOBAL',
  productId: null,
  franchiseeId: null,
  priority: 0,
  status: 0,
  referrerTaxReceiverRole: 'PLATFORM',
  remark: '',
  roleConfigs: []
})

// 角色表单
const roleForm = reactive({
  roleCode: '',
  roleName: '',
  calculateType: 'RATIO',
  value: 0,
  minAmount: null,
  maxAmount: null,
  ladderSteps: [],
  priority: 0
})

// 弹窗状态
const showRoleForm = ref(false)
const isEditingRole = ref(false)
const editingRoleIndex = ref(-1)

// 计算基准金额
const calculationAmount = ref(10000)

// 格式化金额显示
const formatAmount = (amount) => {
  return (amount || 0).toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

// 保留两位小数（更建议用“分”为单位做整数运算，这里先最小改动）
const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100

/**
 * @param role 角色配置
 * @param currentRemaining 当前剩余金额
 * @param base 原始基准金额（calculationAmount）
 */
const calculateSingleRole = (role, currentRemaining, base) => {
  let amount = 0
  let remaining = currentRemaining

  switch (role.calculateType) {
    case 'RATIO': {
      // 固定比例：按 base 计算
      amount = (base || 0) * (role.value || 0) / 100

      // min/max 约束（注意 0 也要生效，所以用 != null）
      if (role.minAmount != null && amount < role.minAmount) amount = role.minAmount
      if (role.maxAmount != null && amount > role.maxAmount) amount = role.maxAmount

      // 不能超过剩余
      amount = Math.min(amount, currentRemaining)
      remaining = currentRemaining - amount
      break
    }

    case 'FIXED': {
      amount = Math.min(role.value || 0, currentRemaining)
      remaining = currentRemaining - amount
      break
    }

    case 'LADDER': {
      // 阶梯分成：按 base 计算，根据 base 匹配阶梯
      const steps = Array.isArray(role.ladderSteps) ? role.ladderSteps : []
      const sortedSteps = [...steps].sort((a, b) => (b.threshold || 0) - (a.threshold || 0))
      const matched = sortedSteps.find(s => base >= (s.threshold || 0))
      amount = base * (matched?.ratio || 0) / 100

      amount = Math.min(amount, currentRemaining)
      remaining = currentRemaining - amount
      break
    }

    case 'MINIMUM': {
      // 最低分成：保底固定金额
      amount = Math.min(role.value || 0, currentRemaining)
      remaining = currentRemaining - amount
      break
    }

    case 'REMAINDER': {
      amount = currentRemaining
      remaining = 0
      break
    }

    case 'RELATIVE_REMAINDER': {
      amount = currentRemaining * (role.value || 0) / 100
      amount = Math.min(amount, currentRemaining)
      remaining = currentRemaining - amount
      break
    }

    default:
      amount = 0
      remaining = currentRemaining
  }

  return { amount: round2(amount), remaining: round2(remaining) }
}

// 计算单个角色的分账金额
const getCalculatedAmount = (_role, index) => {
  if (sortedRoleConfigs.value.length === 0) return 0
  const base = calculationAmount.value
  let remaining = calculationAmount.value

  for (let i = 0; i <= index; i++) {
    const currentRole = sortedRoleConfigs.value[i]
    if (i === index) {
      // 到了目标角色，计算并返回
      const result = calculateSingleRole(currentRole, remaining, base)
      return result.amount
    }
    // 否则计算前面角色的分账并更新剩余金额
    const result = calculateSingleRole(currentRole, remaining, base)
    remaining = result.remaining
    if (remaining <= 0) return 0
  }
  return 0
}

// 获取某个角色计算后的剩余金额
const getRemainingAmount = (index) => {
  if (sortedRoleConfigs.value.length === 0) return 0
  const base = calculationAmount.value
  let remaining = calculationAmount.value

  for (let i = 0; i <= index; i++) {
    const currentRole = sortedRoleConfigs.value[i]
    const result = calculateSingleRole(currentRole, remaining, base)
    remaining = result.remaining
  }

  return Math.round(remaining * 100) / 100
}

// 按优先级排序的角色配置（数字越小优先级越高）
const sortedRoleConfigs = computed(() => {
  return [...form.roleConfigs].sort((a, b) => (a.priority || 0) - (b.priority || 0))
})

// 获取计算类型标签
const getCalculateTypeLabel = (type) => {
  const t = calculateTypes.find(item => item.value === type)
  return t ? t.label : '-'
}

// 计算顺序文本
const calculationOrderText = computed(() => {
  if (sortedRoleConfigs.value.length === 0) return '请添加角色'
  return sortedRoleConfigs.value.map(r => r.roleName).join(' → ')
})

// 格式化分账参数显示
const formatCommissionParams = (role) => {
  switch (role.calculateType) {
    case 'RATIO':
      return `${role.value || 0}%${role.minAmount ? `，最低¥${role.minAmount}` : ''}${role.maxAmount ? `，最高¥${role.maxAmount}` : ''}`
    case 'FIXED':
      return `¥${role.value || 0}`
    case 'LADDER':
      return `${role.ladderSteps?.length || 0}个阶梯`
    case 'MINIMUM':
      return `最低¥${role.value || 0}`
    case 'REMAINDER':
      return '剩余全部分配'
    case 'RELATIVE_REMAINDER':
      return `${role.value || 0}%的剩余金额`
    default:
      return '-'
  }
}

const handleTypeChange = () => {
  form.franchiseeId = null
  form.productId = null
}

// 打开添加角色表单
const openAddRoleForm = () => {
  isEditingRole.value = false
  editingRoleIndex.value = -1
  resetRoleForm()
  showRoleForm.value = true
}

// 重置角色表单
const resetRoleForm = () => {
  roleForm.roleCode = ''
  roleForm.roleName = ''
  roleForm.calculateType = 'RATIO'
  roleForm.value = 0
  roleForm.minAmount = null
  roleForm.maxAmount = null
  roleForm.ladderSteps = []
  roleForm.priority = 0
}

// 关闭角色表单
const closeRoleForm = () => {
  showRoleForm.value = false
}

// 编辑角色
const editRole = (role, index) => {
  isEditingRole.value = true
  editingRoleIndex.value = index
  Object.assign(roleForm, {
    roleCode: role.roleCode,
    roleName: role.roleName,
    calculateType: role.calculateType || 'RATIO',
    value: role.value || 0,
    minAmount: role.minAmount || null,
    maxAmount: role.maxAmount || null,
    ladderSteps: role.ladderSteps ? [...role.ladderSteps] : [],
    priority: role.priority || 0
  })
  showRoleForm.value = true
}

// 确认角色
const confirmRole = () => {
  // 验证
  if (!roleForm.roleCode) {
    ElMessage.warning('请选择角色编码')
    return
  }
  if (!roleForm.roleName.trim()) {
    ElMessage.warning('请输入角色名称')
    return
  }

  // 根据计算类型验证必填字段
  if (['RATIO', 'FIXED', 'MINIMUM'].includes(roleForm.calculateType) && roleForm.value <= 0) {
    ElMessage.warning('请填写有效的金额或比例')
    return
  }

  if (roleForm.calculateType === 'LADDER' && roleForm.ladderSteps.length === 0) {
    ElMessage.warning('请至少添加一个阶梯')
    return
  }

  // 检查角色编码是否已存在（新增时）
  if (!isEditingRole.value) {
    const exists = form.roleConfigs.find(r => r.roleCode === roleForm.roleCode)
    if (exists) {
      ElMessage.warning('该角色编码已存在，不能重复添加')
      return
    }
  }

  const roleConfig = {
    roleCode: roleForm.roleCode,
    roleName: roleForm.roleName,
    calculateType: roleForm.calculateType,
    value: roleForm.value || undefined,
    minAmount: roleForm.calculateType === 'RATIO' && roleForm.minAmount ? roleForm.minAmount : undefined,
    maxAmount: roleForm.calculateType === 'RATIO' && roleForm.maxAmount ? roleForm.maxAmount : undefined,
    ladderSteps: roleForm.calculateType === 'LADDER' && roleForm.ladderSteps.length > 0 ? roleForm.ladderSteps : undefined,
    priority: roleForm.priority || undefined
  }

  if (isEditingRole.value) {
    form.roleConfigs[editingRoleIndex.value] = roleConfig
  } else {
    form.roleConfigs.push(roleConfig)
  }

  closeRoleForm()
}

// 移除角色
const removeRole = (index) => {
  form.roleConfigs.splice(index, 1)
}

// 阶梯分成
const addLadderStep = () => {
  roleForm.ladderSteps.push({ threshold: 0, ratio: 0 })
}

const removeLadderStep = (index) => {
  roleForm.ladderSteps.splice(index, 1)
}

// 保存
const handleSave = async (enable = false) => {
  try {
    // 验证必填字段
    if (!form.ruleName.trim()) {
      ElMessage.warning('请输入规则名称')
      return
    }
    if (form.roleConfigs.length === 0) {
      ElMessage.warning('请至少添加一个角色配置')
      return
    }
    if (form.ruleType === 'FRANCHISEE' && !form.franchiseeId) {
      ElMessage.warning('请选择适用加盟商')
      return
    }
    if (form.ruleType === 'PRODUCT' && !form.productId) {
      ElMessage.warning('请选择适用产品')
      return
    }

    // 验证分账金额不超过基准金额
    const base = calculationAmount.value
    let totalAllocated = 0
    let remaining = base

    for (const role of sortedRoleConfigs.value) {
      const result = calculateSingleRole(role, remaining, base)
      totalAllocated += result.amount
      remaining = result.remaining
    }

    // 如果分账金额总和超过基准金额，阻止保存
    if (totalAllocated > base + 0.01) { // 0.01 容忍度，避免浮点误差
      ElMessage.error(`分账金额总和（¥${formatAmount(totalAllocated)}）超过基准金额（¥${formatAmount(base)}），请调整角色配置`)
      return
    }

    // 如果有剩余金额，阻止保存
    if (remaining > 0.01) {
      ElMessage.error(`基准金额 ¥${formatAmount(base)} 中有 ¥${formatAmount(remaining)} 未分配给任何角色，请调整分配角色`)
      return
    }

    const payload = {
      ruleName: form.ruleName,
      ruleType: form.ruleType,
      priority: form.priority,
      status: enable ? 1 : 0,
      referrerTaxReceiverRole: form.referrerTaxReceiverRole || 'PLATFORM',
      remark: form.remark || undefined,
      franchiseeId: form.ruleType === 'FRANCHISEE' ? form.franchiseeId : undefined,
      productId: form.ruleType === 'PRODUCT' ? form.productId : undefined,
      roleConfigs: form.roleConfigs
    }

    if (isEdit.value) {
      await updateCommissionRule(props.editId, payload)
      ElMessage.success('保存成功')
    } else {
      await createCommissionRule(payload)
      ElMessage.success('创建成功')
    }
    emit('success')
    handleClose()
  } catch (error) {
    ElMessage.error(error.message || '保存失败')
  }
}

// 关闭弹窗
const closeModal = () => {
  emit('update:modelValue', false)
}

const handleClose = () => {
  closeModal()
}

// 加载数据
const loadData = async () => {
  // 如果是编辑模式，加载规则数据
  if (isEdit.value) {
    try {
      const res = await getCommissionRule(props.editId)
      const rule = res.data || res
      form.ruleName = rule.ruleName
      form.ruleType = rule.ruleType
      form.priority = rule.priority
      form.franchiseeId = rule.franchiseeId || null
      form.productId = rule.productId || null
      form.remark = rule.remark || ''
      form.status = rule.status || 0
      form.referrerTaxReceiverRole = rule.referrerTaxReceiverRole || 'PLATFORM'
      form.roleConfigs = (rule.roleConfigs || []).map(r => ({
        roleCode: r.roleCode,
        roleName: r.roleName,
        calculateType: r.calculateType || 'RATIO',
        value: r.value || 0,
        minAmount: r.minAmount || null,
        maxAmount: r.maxAmount || null,
        ladderSteps: r.ladderSteps || [],
        priority: r.priority || 0
      }))
    } catch (error) {
      ElMessage.error(error.message || '加载规则数据失败')
    }
  }
}

// 监听弹窗显示状态
watch(() => props.modelValue, (val) => {
  if (val && !isEdit.value) {
    // 新增模式，重置表单
    form.ruleName = ''
    form.ruleType = 'GLOBAL'
    form.priority = 0
    form.franchiseeId = null
    form.productId = null
    form.remark = ''
    form.status = 0
    form.referrerTaxReceiverRole = 'PLATFORM'
    form.roleConfigs = []
    calculationAmount.value = 10000
  }
  if (val) {
    loadData()
  }
})
</script>
